// Global quick-add overlay (owner 2026-08-17): renders the existing
// DrawerNew create forms ALONE in an app-level right-side overlay — no
// module grid mounts and no tab navigation happens. Reuses the module
// drawers' exact form components by wrapping them in overridden providers:
//   - DrawerContext: isNew/isOpen true, actions close the overlay (the URL
//     carries no /forms/drawer/ segment here).
//   - RightMenuContext: local width state, so the forms' setRightMenuWidth
//     calls (e.g. CompanyDrawerNew's 42%) size THIS drawer instead of the
//     real right menu.
// Submits go through the same create services the module drawer submit
// uses, so Redux grid updates behave identically.
import React, { useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useAppDispatch } from '../../../state_manager/hooks/useAppDispatch.ts'
import type { RootState } from '../../../state_manager/store.ts'
import { Button as AntButton, Modal, Space, Tooltip, message } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimes, faCheck } from '@fortawesome/free-solid-svg-icons'
import Button from '../../components/Button.jsx'
import { portalColors } from '../../../portal-configs/colors.js'
import { DrawerContext } from '../../contexts/DrawerContext.jsx'
import { RightMenuContext } from '../../contexts/RightMenuContext.jsx'
import { useQuickAdd, type QuickAddEntity } from '../../contexts/QuickAddContext.jsx'
import { store } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../auth/authService'
import { assignNewDealSequence } from '../../utils/newDealSequence.ts'
import { createDealService } from '../../../state_manager/state_update_actions/deals/deal_services.ts'
import { createCompanyService } from '../../../state_manager/state_update_actions/companies/company_services.ts'
import { createContactService } from '../../../state_manager/state_update_actions/contacts/contact_services.ts'
import { createFundraisingService } from '../../../state_manager/state_update_actions/fundraising/fundraising_services.ts'
import { Drawer as DealDrawerNew } from '../../../app_modules/deals/components/forms/DrawerNew.tsx'
import { Drawer as CompanyDrawerNew } from '../../../app_modules/companies/components/forms/DrawerNew.tsx'
import { Drawer as ContactDrawerNew } from '../../../app_modules/contacts/components/forms/DrawerNew.tsx'
import { Drawer as FundraisingDrawerNew } from '../../../app_modules/fundraising/components/forms/DrawerNew.tsx'

const TITLES: Record<QuickAddEntity, string> = {
  company: 'New Company',
  contact: 'New Contact',
  deal: 'New Deal',
  prospect: 'New Prospect'
}

const noop = () => {}

/** The create services' shared response shape. */
type CreateResult =
  | Awaited<ReturnType<typeof createDealService>>
  | Awaited<ReturnType<typeof createCompanyService>>
  | Awaited<ReturnType<typeof createContactService>>
  | Awaited<ReturnType<typeof createFundraisingService>>

export function GlobalQuickAddDrawer() {
  const quickAdd = useQuickAdd()
  const dispatch = useAppDispatch()
  const { isAuthenticated } = useSelector((state: RootState) => state.auth)
  const formRef = useRef<{ submit: () => Promise<void> } | null>(null)
  const [loading, setLoading] = useState(false)
  const [width, setWidth] = useState('35%')
  const [confirmCloseVisible, setConfirmCloseVisible] = useState(false)
  // Remount the form fresh each open (parallel of MainTabContainer's
  // per-module newDrawerOpenCount keying).
  const [openCount, setOpenCount] = useState(0)

  const entity = quickAdd?.entity ?? null
  const seed = quickAdd?.seed ?? null

  const close = () => {
    setConfirmCloseVisible(false)
    setWidth('35%')
    setOpenCount((n) => n + 1)
    quickAdd?.closeQuickAdd()
  }

  // The DrawerNew forms read drawer state via useDrawer(); supply the
  // overlay's version of it. closeDrawer closes the overlay.
  const drawerContextValue = useMemo(
    () => ({
      isOpen: true,
      basePath: null,
      recordId: null,
      isNew: true,
      formType: entity,
      pendingNavigation: null,
      openDrawerForNew: noop,
      openDrawerForRecord: noop,
      closeDrawer: close,
      updateDrawerRecord: noop,
      confirmPendingNavigation: noop,
      cancelPendingNavigation: noop,
      getCurrentBasePath: () => '/'
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [entity]
  )

  const rightMenuContextValue = useMemo(
    () => ({
      width,
      setRightMenuWidth: setWidth,
      resetRightMenuWidth: () => setWidth('35%')
    }),
    [width]
  )

  if (!quickAdd || !isAuthenticated || !entity) return null

  const handleSubmit = async (formData: Record<string, unknown>) => {
    const token = getAccessToken(store.getState().auth)
    if (!token) return
    setLoading(true)
    try {
      let result: CreateResult | undefined
      if (entity === 'deal') {
        const state = store.getState()
        assignNewDealSequence(formData, state.deals?.pipeline?.data || [], state.deals?.deal?.data || [])
        result = await createDealService(formData, token, dispatch)
      } else if (entity === 'company') {
        result = await createCompanyService(formData, token, dispatch)
      } else if (entity === 'contact') {
        result = await createContactService(formData, token, dispatch)
      } else if (entity === 'prospect') {
        result = await createFundraisingService(formData, token, dispatch)
      }
      if (result?.data) {
        close()
      } else if (result?.error) {
        message.error(result.error)
      }
    } catch (error) {
      console.error('Quick-add save failed:', error)
      message.error('Failed to save record')
    } finally {
      setLoading(false)
    }
  }

  // key passed directly (not via spread) — React warns on key-in-spread.
  const formKey = `${entity}-${openCount}`
  const formProps = { ref: formRef, onSubmit: handleSubmit, loading }

  let formContent = null
  if (entity === 'deal') formContent = <DealDrawerNew key={formKey} {...formProps} deal={seed} module='deals' />
  else if (entity === 'company') formContent = <CompanyDrawerNew key={formKey} {...formProps} company={null} />
  else if (entity === 'contact') formContent = <ContactDrawerNew key={formKey} {...formProps} contact={null} />
  else if (entity === 'prospect') formContent = <FundraisingDrawerNew key={formKey} {...formProps} prospect={seed} />

  return (
    <>
      {/* Backdrop over the whole app — the form is the only focus */}
      <div className='fixed inset-0 z-[60] bg-black/30' onClick={() => setConfirmCloseVisible(true)} />
      {/* Right-side panel, same chrome as the module drawer (RightMenu) */}
      <div
        data-component='GlobalQuickAddDrawer'
        className='fixed top-10 right-0 bottom-0 z-[61] bg-white border-l border-gray-200 shadow-lg transition-all duration-300 ease-in-out'
        style={{ width, minWidth: 420 }}
      >
        <div className='h-full flex flex-col'>
          <div
            className='flex items-center justify-between px-4 border-b border-gray-200 bg-gradient-to-br from-blue-400 via-blue-500 to-blue-700 gap-2'
            style={{ height: '48px' }}
          >
            <div className='flex-1 min-w-0'>
              <h3 className='text-lg font-semibold text-white truncate'>{TITLES[entity]}</h3>
            </div>
            <div className='flex items-center space-x-2 flex-shrink-0'>
              <Tooltip title='Close'>
                <AntButton
                  size='small'
                  icon={<FontAwesomeIcon icon={faTimes} />}
                  onClick={() => setConfirmCloseVisible(true)}
                  className='bg-white/20 border-white/40 text-white hover:!bg-white/30 hover:!border-white/60 hover:!text-white'
                  style={{ width: '32px', height: '32px', padding: 0, minWidth: 0 }}
                  title='Close'
                />
              </Tooltip>
            </div>
          </div>

          <div className='flex-1 min-h-0 overflow-y-auto'>
            <div className='p-2 h-full'>
              <DrawerContext.Provider value={drawerContextValue}>
                <RightMenuContext.Provider value={rightMenuContextValue}>{formContent}</RightMenuContext.Provider>
              </DrawerContext.Provider>
            </div>
          </div>

          <div className='p-2 border-t border-gray-200 bg-gray-50 flex justify-end'>
            <Space>
              <Button variant='cancel' onClick={() => setConfirmCloseVisible(true)} size='middle'>
                Cancel
              </Button>
              <Button
                variant='save'
                icon={<FontAwesomeIcon icon={faCheck} style={{ color: '#FFFFFF' }} />}
                onClick={() => formRef.current?.submit?.()}
                loading={loading}
                size='middle'
              >
                Save
              </Button>
            </Space>
          </div>
        </div>

        <Modal
          title='Are you sure you want to close this new record?'
          open={confirmCloseVisible}
          onCancel={() => setConfirmCloseVisible(false)}
          closable={false}
          maskClosable={false}
          footer={
            <Space>
              <Button variant='cancel' onClick={() => setConfirmCloseVisible(false)}>
                No
              </Button>
              <Button
                variant='delete'
                onClick={close}
                style={{
                  backgroundColor: portalColors.error,
                  borderColor: portalColors.error,
                  color: '#FFFFFF'
                }}
                className='hover:!bg-red-600 hover:!border-red-600'
              >
                Yes
              </Button>
            </Space>
          }
        >
          <p>Any unsaved changes will be lost.</p>
        </Modal>
      </div>
    </>
  )
}
